import { useCallback, useEffect, useRef, useState } from 'react'
import { copyText } from '../ui/clipboard'
import { relayRead, relayRequest } from '../transport/relay'
import { onStreamOpen } from './commandStream'
import { noteInstall, useInstallJob, type InstallExtra, type InstallJob } from './cliInstallJobs'
import type { InstallItem } from '../transport/api'

/**
 * "THIS MACHINE DOES NOT HAVE THAT CLI — INSTALL IT" (owner relaying a user, 2026-10-03:
 * 在創建 Agent 的頁面裡, 機器沒有安裝該 Agent 的 CLI 工具, 並沒有任何錯誤回報或提醒需要安裝).
 *
 * Shown when the machine's own check (/agent/cli_status — the launch PATH, then a login
 * shell: the same search the launch does) did not find the chosen CLI, and again when a
 * create came back cli_not_found. It says what to run ON THAT MACHINE.
 *
 * INSTALL FOR ME (owner, 2026-10-16: 把 check again 改為 install for me). Where the machine
 * says it can — its cli_status row carries `install`, the command IT runs for that CLI — the
 * button asks the machine to run it (POST /agent/cli_install, the CLI's name only). The
 * machine pushes how it is going (cliInstallJobs); when the CLI is there the type list is
 * asked again and this box goes away. The command shown is the machine's own string, so what the
 * box shows and what the button runs cannot differ. An edge too old to say `install` (or a
 * CLI with no installer, Antigravity) keeps "Check again".
 *
 * The commands below are the fallback for those older edges: each vendor's own installer,
 * read out of the installed CLIs on aowl (claude.ai/install.sh, cursor.com/install,
 * x.ai/cli/install.sh) and Codex's npm package. Antigravity ships no one-line installer we
 * could confirm, so it gets its docs page. Hermes Agent's is the one Nous Research's own
 * README gives; the copy on spark-244c reports `Install method: git`, which is what that
 * script sets up. Muse Code's is Meta's own for macOS and Linux (dev.meta.ai/install.sh, read
 * 2026-10-16 — what aowl's copy was installed with). Muse ships one installer per system
 * (owner, 2026-10-16: 有分 Mac、Windows 還有 Linux): a Windows machine's edge reports
 * PowerShell's `irm https://dev.meta.ai/install.ps1 | iex` as its `install`, and that is the
 * line this box shows there; the docs link is the page listing both.
 */
const CLI_INSTALL: Record<string, { label: string; command?: string; docs: string }> = {
  claude: {
    label: 'Claude Code',
    command: 'curl -fsSL https://claude.ai/install.sh | bash',
    docs: 'https://docs.anthropic.com/en/docs/claude-code/setup',
  },
  codex: { label: 'Codex', command: 'npm install -g @openai/codex', docs: 'https://github.com/openai/codex' },
  cursor: { label: 'Cursor', command: 'curl https://cursor.com/install -fsS | bash', docs: 'https://cursor.com/cli' },
  grok: { label: 'Grok', command: 'curl -fsSL https://x.ai/cli/install.sh | bash', docs: 'https://x.ai/cli' },
  hermes: {
    label: 'Hermes Agent',
    command: 'curl -fsSL https://hermes-agent.nousresearch.com/install.sh | bash',
    docs: 'https://hermes-agent.nousresearch.com/docs/',
  },
  antigravity: { label: 'Antigravity', docs: 'https://antigravity.google/docs' },
  muse: {
    label: 'Muse Code',
    command: 'curl -fsSL https://dev.meta.ai/install.sh | bash',
    docs: 'https://dev.meta.ai/docs/overview',
  },
}

const labelOf = (k: string | undefined): string =>
  (k && CLI_INSTALL[k === 'agy' ? 'antigravity' : k]?.label) || k || 'Another CLI'

/** What went wrong, in the words that fit how it ended. */
function failureLine(j: InstallJob, label: string, machine: string): string {
  if (j.error === 'timed_out') return 'The install took too long and was stopped:'
  if (j.error === 'stopped') return 'The install stopped before it finished.'
  if (j.signal) return `The install was stopped by the system (${j.signal}):`
  // It said it worked, and the CLI is still nowhere the launch looks.
  if (j.error === 'not_found_after' && j.exit_code === 0) return `The installer finished, but ${label} is still not found on ${machine}:`
  return 'The install did not finish:'
}

export function CliMissing({
  cli,
  machine,
  deviceId,
  install,
  includes,
  checking,
  onCheckAgain,
  onInstalled,
}: {
  cli: string
  machine: string
  /** The machine the box is about — the install is asked of IT, not the relay's target. */
  deviceId: string
  /** The command this machine runs for "Install for me"; absent = it cannot (older edge). */
  install?: string | undefined
  /** What that button installs there, one row each; absent on an older edge. */
  includes?: InstallItem[] | undefined
  checking: boolean
  onCheckAgain: () => void
  /** The CLI is there now: ask the type list again. */
  onInstalled: () => unknown
}) {
  const wire = cli === 'antigravity' ? 'agy' : cli
  const how = CLI_INSTALL[cli === 'agy' ? 'antigravity' : cli]
  const [copied, setCopied] = useState(false)
  const job = useInstallJob(deviceId, wire)
  const [posting, setPosting] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  // Done: the type list is asked again, once per run — and when it sees the CLI, this box
  // is gone. Until that answer is in, the button says so rather than offering to install
  // again; if the list still does not see the CLI, the button comes back.
  const installedRef = useRef(onInstalled)
  useEffect(() => {
    installedRef.current = onInstalled
  })
  const [finishing, setFinishing] = useState(false)
  const doneRun = job?.state === 'done' ? job.started_at : null
  useEffect(() => {
    if (doneRun === null) return
    setFinishing(true)
    void Promise.resolve(installedRef.current()).finally(() => {
      if (alive.current) setFinishing(false)
    })
  }, [doneRun])

  /*
   * THE FLOOR under the events — and no clock (the owner's standing rule; BackupForm's
   * shape). While an install runs, its machine is asked again at the moments a lost event
   * becomes knowable: the account stream reopening (anything pushed while it was down is
   * gone) and the app coming back into view.
   */
  const runningRun = job?.state === 'running' ? job.started_at : null
  const reask = useCallback(
    (run: number) =>
      void relayRead(`/agent/cli_install?cli=${encodeURIComponent(wire)}`, deviceId).then(
        (a) => {
          const s = a.status === 200 ? (a.body as { state?: string } | null) : null
          // No record of it: the machine's edge restarted mid-install.
          if (s?.state === 'idle') {
            noteInstall(deviceId, { cli: wire, state: 'failed', started_at: run, error: 'stopped', tail: [] })
          } else noteInstall(deviceId, s)
        },
        () => {
          /* the next opening asks again */
        },
      ),
    [wire, deviceId],
  )
  // The box OPENING on a run already under way (the sheet closed and opened again, or the
  // type picked again) is a moment its end may have been missed: ask once, now.
  const runAtOpen = useRef(runningRun)
  useEffect(() => {
    if (runAtOpen.current !== null) reask(runAtOpen.current)
  }, [reask])
  useEffect(() => {
    if (runningRun === null) return
    const again = () => reask(runningRun)
    const offOpen = onStreamOpen(again)
    const onVisible = () => {
      if (document.visibilityState === 'visible') again()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      offOpen()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [runningRun, reask])

  const start = async () => {
    setNote(null)
    setPosting(true)
    try {
      const a = await relayRequest('POST', '/agent/cli_install', { cli: wire }, undefined, deviceId)
      if (!alive.current) return
      const s = (a.body ?? {}) as { cli?: string }
      if (a.status === 409) setNote(`${labelOf(s.cli)} is being installed on ${machine}. Try again when it finishes.`)
      else if (a.status !== 200) setNote(`${machine} could not start the install. Run the command above on it.`)
      else noteInstall(deviceId, a.body)
    } catch {
      if (alive.current) setNote(`${machine} did not answer. Try again.`)
    } finally {
      if (alive.current) setPosting(false)
    }
  }

  if (!how) return null
  const canInstall = !!install
  const command = install || how.command
  const running = posting || job?.state === 'running'
  const busy = running || finishing
  const failed = !busy && job?.state === 'failed' ? job : null
  return (
    <div className="cli-missing" role="alert" data-testid="cli-missing">
      <p className="cli-missing-title">
        {how.label} is not installed on {machine}
      </p>
      {command ? (
        <>
          <p className="cli-missing-line">
            {canInstall ? `Run this on ${machine}:` : `Run this on ${machine}, then check again:`}
          </p>
          <div className="cli-missing-cmd">
            <code>{command}</code>
            <button
              type="button"
              className="cli-missing-copy"
              onClick={() => void copyText(command).then((r) => setCopied(r.ok))}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </>
      ) : (
        <p className="cli-missing-line">Install it on {machine}, then check again.</p>
      )}
      {canInstall && !!includes?.length && (
        <InstallRows items={includes} cli={wire} job={running || failed ? job : null} />
      )}
      {running && job?.state === 'running' && job.last_line && (
        <p className="cli-missing-progress" data-testid="cli-install-progress">
          {job.last_line}
        </p>
      )}
      {failed && (
        <>
          <p className="cli-missing-line">{failureLine(failed, how.label, machine)}</p>
          {!!failed.tail?.length && <pre className="cli-missing-log">{failed.tail.join('\n')}</pre>}
        </>
      )}
      {note && <p className="cli-missing-line">{note}</p>}
      <div className="cli-missing-acts">
        <a href={how.docs} target="_blank" rel="noreferrer">
          Install guide
        </a>
        {canInstall ? (
          <button type="button" className="sheet-btn" disabled={busy} onClick={() => void start()}>
            {running ? 'Installing…' : finishing ? 'Checking…' : failed ? 'Try again' : 'Install for me'}
          </button>
        ) : (
          <button type="button" className="sheet-btn" disabled={checking} onClick={onCheckAgain}>
            {checking ? 'Checking…' : 'Check again'}
          </button>
        )}
      </div>
    </div>
  )
}

type RowState = InstallExtra['state']
const MARKS: Record<RowState | 'idle', string> = { idle: '•', pending: '•', running: '…', done: '✓', failed: '✕', skipped: '–' }

/**
 * WHAT THE BUTTON INSTALLS — owner, 2026-10-26: 讓使用者知道按下按鈕之後哪些東西會被安裝. One row
 * each, as the machine lists it (cli_status `install_includes`): a name and one short line. While
 * it installs, the same rows are its checklist — the CLI until its extras begin, then each extra
 * as the machine reports it.
 */
function InstallRows({ items, cli, job }: { items: InstallItem[]; cli: string; job: InstallJob | null }) {
  const stateOf = (item: InstallItem): RowState | 'idle' => {
    if (!job) return 'idle'
    const extra = job.extras?.find((x) => x.key === item.key)
    if (extra) return extra.state
    if (item.key !== cli) return 'idle'
    if (job.state === 'failed') return 'failed'
    if (job.state === 'done') return 'done'
    return job.extras?.some((x) => x.state !== 'pending') ? 'done' : 'running'
  }
  return (
    <div className="cli-install-includes" data-testid="cli-install-includes">
      <p className="cli-missing-line">Install for me installs:</p>
      <ul className="cli-install-rows">
        {items.map((item) => {
          const st = stateOf(item)
          return (
            <li key={item.key} className="cli-install-row" data-state={st}>
              <span className="cli-install-mark" aria-hidden="true">
                {MARKS[st]}
              </span>
              <span className="cli-install-text">
                <span className="cli-install-name">{item.label}</span>
                <span className="cli-install-detail">{item.detail}</span>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * AN EXTRA THAT COULD NOT BE ADDED, after the CLI itself was. The box above goes away once the
 * CLI is there, so this says, in its place, what was left out on that machine, why, and the
 * line to run there by hand (Ollama on a Linux machine whose sudo asks for a password, say).
 */
export function InstallExtrasNote({ cli, machine, deviceId }: { cli: string; machine: string; deviceId: string }) {
  const wire = cli === 'antigravity' ? 'agy' : cli
  const job = useInstallJob(deviceId, wire)
  const [copied, setCopied] = useState<string | null>(null)
  const left = job?.state === 'done' ? (job.extras ?? []).filter((x) => x.state === 'failed') : []
  if (!left.length) return null
  return (
    <div className="cli-missing cli-extras-note" role="status" data-testid="cli-extras-note">
      {left.map((x) => (
        <div key={x.key} className="cli-extras-item">
          <p className="cli-missing-title">
            {x.label} was not installed on {machine}
          </p>
          {x.detail && <p className="cli-missing-line">{x.detail}</p>}
          {x.command && (
            <div className="cli-missing-cmd">
              <code>{x.command}</code>
              <button
                type="button"
                className="cli-missing-copy"
                onClick={() => void copyText(x.command!).then((r) => setCopied(r.ok ? x.key : null))}
              >
                {copied === x.key ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
